/**
 * Pure helpers for the `grid_input` resource type.
 *
 * Spec: docs/onboarding/API/Grid.md
 *
 * Two metadata groups carry layout + binding:
 *   - `grid_filled` → display-only / read-only columns
 *   - `default`    → editable cells, repeated per row
 *
 * Other groups (anything whose `title` starts with `_`, e.g. `_lookup`) are
 * server context and never rendered.
 */

import type { FormField, FormGroup, FormSchema, GridSubmissionRow } from '../types';

/** Group title used by the backend for the read-only columns. */
export const GRID_FILLED_GROUP = 'grid_filled';
/** Group title used by the backend for the per-row editable inputs. */
export const GRID_DEFAULT_GROUP = 'default';

/**
 * Splits a `grid_input` schema into its two visible column lists plus the
 * id column (the `grid_filled` item with `uidt: "ID"`). Returns nulls when
 * the schema is misconfigured so callers can surface a clear error rather
 * than render a broken table.
 */
export interface GridColumns {
  /** Read-only / display columns (always include the id column). */
  gridFilled: FormField[];
  /** Editable column definitions, one cell per row. */
  defaultItems: FormField[];
  /**
   * The row identifier — `grid_filled.items[]` entry with `uidt: "ID"`.
   * Its `title` is the property name on `options` rows and submission rows,
   * and must be present in every save / workflow body (spec §4, §6.3).
   */
  idColumn: FormField | null;
}

const findGroupItems = (schema: FormSchema, title: string): FormField[] => {
  const group = schema.columns.find((g: FormGroup) => g.title === title);
  return group ? group.items : [];
};

export const resolveGridColumns = (schema: FormSchema): GridColumns => {
  const gridFilled = findGroupItems(schema, GRID_FILLED_GROUP);
  const defaultItems = findGroupItems(schema, GRID_DEFAULT_GROUP);
  const idColumn =
    gridFilled.find((item) => (item.uidt || '').toLowerCase() === 'id') ?? null;
  return { gridFilled, defaultItems, idColumn };
};

/**
 * Stable React key for a row. The id value from `options` is the canonical
 * key per spec §4 ("That column's `title` is the stable row key"); we fall
 * back to `column_name` for resources that key under that instead.
 */
export const rowKeyFromOption = (
  option: Record<string, unknown>,
  idColumn: FormField,
): string | null => {
  const v = readOptionValue(option, idColumn);
  if (v === undefined || v === null || v === '') return null;
  return String(v);
};

/**
 * Builds a `rowKey → submission record` map. Each submission entry's value
 * for the id column joins it to a row from `options`. Submissions whose
 * id can't be resolved are dropped (caller surfaces a generic load error
 * if needed).
 */
export const indexSubmissionsByRowKey = (
  submissions: GridSubmissionRow[] | undefined,
  idColumn: FormField,
): Map<string, GridSubmissionRow> => {
  const map = new Map<string, GridSubmissionRow>();
  if (!submissions) return map;
  for (const sub of submissions) {
    const v = readOptionValue(sub, idColumn);
    if (v === undefined || v === null || v === '') continue;
    map.set(String(v), sub);
  }
  return map;
};

/**
 * Extract the raw id value (number or string, as sent by the server) for
 * a row, preserving the original type so the save body matches the column's
 * declared `dt`. Tries `title` then `column_name` so the resource convention
 * doesn't matter to callers.
 */
export const idValueForRow = (
  option: Record<string, unknown>,
  idColumn: FormField,
): number | string | null => {
  const v = readOptionValue(option, idColumn);
  if (typeof v === 'number') return v;
  if (typeof v === 'string' && v.length > 0) return v;
  return null;
};

/** True when a column should render as a plain-text cell. */
export const isLabelColumn = (item: FormField): boolean =>
  item.display_type === 'label';

/**
 * Read the value of a column from one `options` row. Per spec §4, the
 * binding key may be the column's `title` OR `column_name` — different
 * resources pick different conventions. Prefer `title` (the canonical
 * key), fall back to `column_name` for resources that store under that.
 */
export const readOptionValue = (
  option: Record<string, unknown>,
  item: FormField,
): unknown => {
  const byTitle = option[item.title];
  if (byTitle !== undefined && byTitle !== null && byTitle !== '') return byTitle;
  if (item.column_name && item.column_name !== item.title) {
    return option[item.column_name];
  }
  return byTitle;
};

/**
 * True when a column should render as a "view document" link. Accepts
 * `versioned_document_viewer` (canonical, per Grid.md §4) plus the more
 * lenient `document_viewer` and `link` variants that appear in the wild —
 * any of these signal "this cell points to an uploaded document".
 */
export const isVersionedDocumentViewer = (item: FormField): boolean => {
  const dt = (item.display_type || '').toLowerCase();
  return dt === 'versioned_document_viewer' || dt === 'document_viewer' || dt === 'link';
};

/**
 * Header label per spec §4: `display_title` with `title` fallback.
 * Returns an empty string if neither is set so the caller can decide
 * whether to omit the header cell.
 */
export const headerLabel = (item: FormField): string => {
  if (typeof item.display_title === 'string' && item.display_title.length > 0) {
    return item.display_title;
  }
  return item.title || '';
};
